"use client";
import { Button, H1, H3, Input, Select } from "@template/components";
import * as S from "./styles.ts";
import { useEffect, useState } from "react";
import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import { useRouter } from "@/lib/helpers/hooks/useRouter.ts";

import { postCartItem } from "@/redux/slices/user/thunks/postCartItem.ts";
import { getAreaCodes } from "@/redux/slices/numbers/thunks/getAreaCodes.ts";
import { IButtonProps } from "@template/components/Button";

type TSearchLink = {
  label: string;
  placeholder: string;
  type: "number-purchase" | "number-transfer";
  button: IButtonProps;
};

interface ISearchLinksSectionComponentProps {
  title?: string;
  links?: TSearchLink[];
}

const SearchLinkNumberPurchase = ({
  label,
  placeholder,
  button,
  type,
}: TSearchLink) => {
  const dispatch = useTypedDispatch();
  const router = useRouter();

  const countries = useTypedSelector((state) => state.numbers.countries);
  const currency = useTypedSelector(
    (state) => state.store.site_version.currency
  );

  const [selected, setSelected] = useState("");

  const isUs = currency === "USD";
  const targetCountryValue = isUs ? "233" : "77";

  const foundCountry = countries.find((c) => c.value === targetCountryValue);
  const areaCodes =
    foundCountry?.area_codes.map((code) => ({
      label: code.name,
      key: code.area_code,
    })) || [];

  const handleSubmit = (e) => {
    e.preventDefault();

    let phoneNumberSearchHref = `/phone-number/all-numbers`;
    if (selected) {
      phoneNumberSearchHref += `?countryCode=${targetCountryValue}&areaCode=${selected}`;
    }

    router.push(phoneNumberSearchHref);

    return false;
  };

  useEffect(() => {
    dispatch(getAreaCodes(targetCountryValue));
  }, [targetCountryValue]);

  return (
    <S.SearchCard>
      <H3>{label}</H3>
      <S.InputSection onSubmit={handleSubmit}>
        <Select
          placeholder={placeholder}
          fullWidth
          options={[
            {
              label: "Select an Option",
              key: "",
              disabled: true,
            },
            ...areaCodes,
          ]}
          onChange={(val) => setSelected(val)}
          selected={selected}
          search
        />
        <Button
          size="lg"
          variant={button?.variant}
        >
          {button?.children}
        </Button>
      </S.InputSection>
    </S.SearchCard>
  );
};

const SearchLinkNumberTransfer = ({
  label,
  placeholder,
  button,
  type,
}: TSearchLink) => {
  const dispatch = useTypedDispatch();

  const [error, setError] = useState("");
  const [number, setNumber] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAddTransfer = async (e) => {
    e.preventDefault();

    if (loading) return;
    setLoading(true);
    await dispatch(
      postCartItem({
        itemType: 15,
        itemSubType: 0,
        itemData: {
          number: number,
        },
      })
    ).then((r: any) => {
      if (r.error) {
        setError(r.payload);
      }
    });

    setLoading(false);

    return false;
  };

  return (
    <S.SearchCard>
      <H3>{label}</H3>
      <S.InputSection onSubmit={handleAddTransfer}>
        <Input
          error={error}
          placeholder={placeholder}
          fullWidth
          value={number}
          onChange={(val) => {
            setNumber(val);
            setError("");
          }}
        />
        <Button
          size="lg"
          variant={button?.variant}
          type="submit"
        >
          {button?.children}
        </Button>
      </S.InputSection>
    </S.SearchCard>
  );
};

const SearchLinksSectionComponent = ({
  title,
  links,
}: ISearchLinksSectionComponentProps) => {
  return (
    <S.SearchLinksSectionWrapper>
      <S.SearchLinksSectionContainer>
        <S.TitleSection>
          <H1 as="h2">{title}</H1>
        </S.TitleSection>
        <S.CardsSection>
          {links?.map(({ key, ...link }: any, index) =>
            link.type === "number-purchase" ? (
              <SearchLinkNumberPurchase
                key={index}
                {...link}
              />
            ) : link.type === "number-transfer" ? (
              <SearchLinkNumberTransfer
                key={index}
                {...link}
              />
            ) : null
          )}
        </S.CardsSection>
      </S.SearchLinksSectionContainer>
    </S.SearchLinksSectionWrapper>
  );
};

export default SearchLinksSectionComponent;
